import React, { useState, useRef, useEffect } from "react";
import {
    Button,
    Checkbox,
    AcceptIcon,
    SendIcon,
    CloseIcon,
    Divider,
    ProviderConsumer,
} from "@fluentui/react-northstar";
import SlateEditor from "../../Wysiwyg/SlateEditor";
import { markdownToSlate, slateToMd } from "../../Wysiwyg/slate-editor-helpers";
import { getFileIcon } from "../../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent";
import EditableTicketAttachment, { EditTicketAttachmentsProps } from "./EditableTicketAttachment";
import { useFileDrop } from "./useFileDrop";

const imagePreviewExtensions = new Set(
    "jpg gif png jpeg bmp ico tif tiff psd ps ai".split(" ")
);

const isImageFile = (file: File) => {
    if (file.type.startsWith("image/")) return true;
    const m = /\.([^.]+)$/.exec(file.name);
    return m ? imagePreviewExtensions.has(m[1].toLowerCase()) : false;
};

type NewFilePreviewProps = {
    file: File;
    onRemove: () => void;
    disabled: boolean;
    globalTheme: any;
    /** 0–100 upload progress; undefined when not uploading */
    uploadProgress?: number;
};

const NewFilePreview: React.FC<NewFilePreviewProps> = ({ file, onRemove, disabled, globalTheme, uploadProgress }) => {
    const showImagePreview = isImageFile(file);
    const [objectUrl] = useState<string | null>(() =>
        showImagePreview ? URL.createObjectURL(file) : null
    );
    const isUploading = uploadProgress !== undefined && uploadProgress < 100;

    useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    return (
        <div className="w-1/2 md:w-1/4 p-2">
            <div className="shadow-sm border overflow-hidden rounded-md" style={{backgroundColor: globalTheme.siteVariables.colorScheme.default.background}}>
                {showImagePreview && objectUrl ? (
                    <div
                        style={{
                            backgroundImage: `url(${objectUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            width: '100%',
                            aspectRatio: '16/9',
                            minHeight: '120px',
                            position: 'relative',
                        }}
                    >
                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background3,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${uploadProgress ?? 0}%`,
                                        backgroundColor: globalTheme.siteVariables.colorScheme.brand.foreground1,
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="relative flex items-center justify-center"
                        style={{ padding: '1rem', minHeight: '120px' }}
                    >
                        {getFileIcon(file.name, { file }, globalTheme)}
                        {isUploading && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background3,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        height: '100%',
                                        width: `${uploadProgress ?? 0}%`,
                                        backgroundColor: globalTheme.siteVariables.colorScheme.brand.foreground1,
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div className="flex min-w-0 items-center">
                    <div className="min-w-0 flex-1 truncate px-2" title={file.name}>
                        {file.name}
                    </div>
                    <div className="shrink-0">
                        <Button
                            icon={<CloseIcon />}
                            text
                            iconOnly
                            title="Remove"
                            onClick={onRemove}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

type EditableTicketChatContentProps = {
    message: string;
    setMessage: (message: string) => void;
    assignees: PlatformUser[];
    editedAttachmentsList: EditTicketAttachmentsProps[];
    setEditedAttachmentsList: (attachments: EditTicketAttachmentsProps[]) => void;
    onCancel: () => void;
    comment: any;
    handleUpdateMessage: (newFilesToUpload: File[]) => void;
    handleSendMessage: (newFilesToUpload: File[]) => void;
    isPublic: boolean;
    setIsPublic: (isPublic: boolean) => void;
    loading?: boolean;
    /** When loading, which action to display: "Saving..." or "Sending..." */
    actionStatus?: "saving" | "sending";
    /** Per-file upload progress 0–100, keyed by new-file index */
    uploadProgress?: Record<number, number>;
    hasChanges?: boolean;
};

export const EditableTicketChatContent: React.FC<EditableTicketChatContentProps> = ({
    message,
    setMessage,
    assignees,
    editedAttachmentsList,
    setEditedAttachmentsList,
    onCancel,
    comment,
    handleUpdateMessage,
    handleSendMessage,
    isPublic,
    setIsPublic,
    loading = false,
    actionStatus,
    uploadProgress = {},
    hasChanges = false,
}) => {
    type PendingNewFile = { key: string; file: File };
    const nextNewFileKeyRef = useRef(0);
    const [newFilesToUpload, setNewFilesToUpload] = useState<PendingNewFile[]>([]);
    const canSave = message.trim() !== '' && (hasChanges || newFilesToUpload.length > 0);
    const droppedIndexRef = useRef(0);

    const appendNewFiles = (files: File[]) => {
        setNewFilesToUpload((prev) => [
            ...prev,
            ...files.map((file) => ({
                key: `new-file-${nextNewFileKeyRef.current++}`,
                file,
            })),
        ]);
    };

    const handleImagePaste = (file: File) => {
        const result = new File(
            [file],
            `image-${droppedIndexRef.current.toString().padStart(3, "0")}.png`,
            { type: file.type }
        );
        appendNewFiles([result]);
        droppedIndexRef.current += 1;
    };

    const { isDragging, handleDragOver, handleDragEnter, handleDragLeave, handleDrop } =
        useFileDrop({
            onFilesDropped: (files: File[]) => {
                appendNewFiles(files);
            },
            disabled: loading,
        });

    const removeNewFile = (key: string) => {
        setNewFilesToUpload((prev) => prev.filter((entry) => entry.key !== key));
    };

    return (
        <ProviderConsumer
            render={(globalTheme) => {
                return (
                    <div
                        className="shadow-sm w-full min-w-0"
                        style={{
                            maxWidth: "100%",
                            backgroundColor:
                                globalTheme.siteVariables.colorScheme.default.background,
                            borderStyle: "dashed",
                            borderWidth: isDragging ? 2 : 0,
                            borderColor: isDragging
                                ? globalTheme.siteVariables.colorScheme.brand.foreground1
                                : "transparent",
                        }}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <SlateEditor
                            className="w-full min-w-0"
                            onChange={(data) => {
                                let mk = slateToMd(data);
                                setMessage(mk);
                            }}
                            slateValue={markdownToSlate(message)}
                            usersInMention={assignees}
                            isFocus={true}
                            noImage={true}
                            onImagePaste={handleImagePaste}
                            minHeight="4rem"
                            disabled={loading}
                        />

                        {(editedAttachmentsList.length > 0 || newFilesToUpload.length > 0) && (
                            <div className="flex flex-wrap">
                                {editedAttachmentsList.map((attachment: EditTicketAttachmentsProps) => (
                                    <EditableTicketAttachment
                                        key={attachment.Id}
                                        {...attachment}
                                        onRemove={() => {
                                            setEditedAttachmentsList(
                                                editedAttachmentsList.filter((att) => att.Id !== attachment.Id)
                                            );
                                        }}
                                        disabled={loading}
                                    />
                                ))}
                                {newFilesToUpload.map((entry, index) => (
                                    <NewFilePreview
                                        key={entry.key}
                                        file={entry.file}
                                        onRemove={() => removeNewFile(entry.key)}
                                        disabled={loading}
                                        globalTheme={globalTheme}
                                        uploadProgress={uploadProgress[index]}
                                    />
                                ))}
                            </div>
                        )}

                        <div
                            className="flex justify-between p-2 border-t border-b"
                            style={{
                                borderBottomColor:
                                    globalTheme.siteVariables.colorScheme.brand.foreground1,
                            }}
                        >
                            <div>
                                <Checkbox 
                                    label="Internal Note" 
                                    checked={!isPublic}
                                    disabled={loading}
                                    onChange={(_e, data) => {
                                        const isChecked = data?.checked ?? false;
                                        setIsPublic(!isChecked);
                                    }}
                                />
                            </div>
                            
                            <div className="flex action-buttons">
                                {loading && actionStatus ? (
                                    <span style={{ alignSelf: "center" }}>
                                        {actionStatus === "saving" ? "Saving..." : "Sending..."}
                                    </span>
                                ) : (
                                    <>
                                        <Button
                                            icon={<CloseIcon />}
                                            text
                                            iconOnly
                                            title="Cancel"
                                            onClick={onCancel}
                                            disabled={loading}
                                        />
                                        <Button
                                            icon={<AcceptIcon />}
                                            text
                                            iconOnly
                                            title="Save"
                                            onClick={() =>
                                                handleUpdateMessage(newFilesToUpload.map((e) => e.file))
                                            }
                                            disabled={!canSave || loading}
                                        />
                                        <Divider vertical />
                                        <Button
                                            icon={<SendIcon />}
                                            text={!canSave || loading}
                                            primary={canSave && !loading}
                                            iconOnly
                                            title="Send"
                                            onClick={() =>
                                                handleSendMessage(newFilesToUpload.map((e) => e.file))
                                            }
                                            disabled={!canSave || loading}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }}
        />
    );
};
